import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaCheck, FaStar, FaChartLine, FaClipboardCheck } from 'react-icons/fa';
import { motion, useReducedMotion } from 'framer-motion';
import SEO from '../components/SEO';
import HowItWorks from '../components/HowItWorks';
import UserCategories from '../components/UserCategories';
import HelpCategories from '../components/HelpCategories';
import FAQ from '../components/FAQ';
import Partners from '../components/Partners';
import ContactForm from '../components/ContactForm';
import AppShowcasePhone from '../components/AppShowcasePhone';
import AppDownloadPopup from '../components/AppDownloadPopup';
import AppCtaLink from '../components/AppCtaLink';
import CrisisSupport from '../components/CrisisSupport';
import { Reveal, staggerParent, staggerItem } from '../components/motion/Reveal';
import AuroraCanvas from '../components/motion/AuroraCanvas';
import { SITE_NAME, SITE_URL } from '../config/site';
import { getLocaleScreens } from '../utils/localeScreens';

const StarRating = ({ rating }) => (
  <div className="testimonial-rating" aria-label={`${rating} out of 5`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <svg
        key={i}
        className={`star-icon ${i <= rating ? 'is-filled' : ''}`}
        width="16"
        height="16"
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M12 2.5l2.7 6.2 6.8.6-5.2 4.5 1.5 6.6L12 16.8 6.2 20.4l1.5-6.6L2.5 9.3l6.8-.6L12 2.5z" />
      </svg>
    ))}
  </div>
);

const HomePage = () => {
  const { t } = useTranslation();
  const { language } = useParams();
  const reduceMotion = useReducedMotion();
  const localeScreens = useMemo(() => getLocaleScreens(language), [language]);
  const valueScreens = useMemo(() => {
    const copy = t('home.value_section.screens', { returnObjects: true, defaultValue: [] });
    const captions = Array.isArray(copy) ? copy : [];

    return localeScreens.map((src, index) => ({
      src,
      title: captions[index]?.title || `Screen ${index + 1}`,
      description: captions[index]?.description || '',
      eager: index === 0
    }));
  }, [localeScreens, t]);
  const [spot, setSpot] = useState({ x: 78, y: 18 });
  const sectionRefs = useRef([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const testimonialsScrollRef = useRef(null);
  const [activeTestimonialIndex, setActiveTestimonialIndex] = useState(0);

  const homeJsonLd = useMemo(() => {
    const faqItems = t('faq.questions', { returnObjects: true, defaultValue: [] });
    const faqPage =
      Array.isArray(faqItems) && faqItems.length
        ? {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqItems.slice(0, 10).map((item) => ({
              '@type': 'Question',
              name: item.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: String(item.answer || '').replace(/\n/g, '<br/>')
              }
            }))
          }
        : null;

    return [
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: SITE_NAME,
        url: SITE_URL,
        logo: `${SITE_URL}/logo512.png`,
        sameAs: [
          'https://www.linkedin.com/company/aipsyhelp',
          'https://www.facebook.com/PsyHelpUA/',
          'https://www.youtube.com/@AIPSYHELP',
          'https://apps.apple.com/app/id6748014564',
          'https://play.google.com/store/apps/details?id=com.aipsyhelp.app'
        ]
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: SITE_NAME,
        url: SITE_URL
      },
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: SITE_NAME,
        applicationCategory: 'HealthApplication',
        operatingSystem: 'iOS, Android, Web',
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'EUR',
          availability: 'https://schema.org/OnlineOnly',
          category: 'Free trial / start free. Paid plans are shown in App Store, Google Play or a B2B proposal.',
          url: `${SITE_URL}/${language || 'en'}`
        },
        sameAs: [
          'https://www.linkedin.com/company/aipsyhelp',
          'https://apps.apple.com/app/id6748014564',
          'https://play.google.com/store/apps/details?id=com.aipsyhelp.app'
        ]
      },
      ...(faqPage ? [faqPage] : [])
    ];
  }, [t, language]);

  const testimonials = useMemo(() => [
    {
      name: t('home.testimonials.users.0.name'),
      role: t('home.testimonial_roles.civilian'),
      text: t('home.testimonials.users.0.text'),
      rating: 5
    },
    {
      name: t('home.testimonials.users.1.name'),
      role: t('home.testimonial_roles.military'),
      text: t('home.testimonials.users.1.text'),
      rating: 5
    },
    {
      name: t('home.testimonials.users.2.name'),
      role: t('home.testimonial_roles.medical'),
      text: t('home.testimonials.users.2.text'),
      rating: 5
    },
    {
      name: t('home.testimonials.users.3.name'),
      role: t('home.testimonial_roles.student'),
      text: t('home.testimonials.users.3.text'),
      rating: 4
    },
    {
      name: t('home.testimonials.users.4.name'),
      role: t('home.testimonial_roles.entrepreneur'),
      text: t('home.testimonials.users.4.text'),
      rating: 5
    },
    {
      name: t('home.testimonials.users.5.name'),
      role: t('home.testimonial_roles.veteran'),
      text: t('home.testimonials.users.5.text'),
      rating: 5
    },
    {
      name: t('home.testimonials.users.6.name'),
      role: t('home.testimonial_roles.teacher'),
      text: t('home.testimonials.users.6.text'),
      rating: 4
    },
    {
      name: t('home.testimonials.users.7.name'),
      role: t('home.testimonial_roles.it_specialist'),
      text: t('home.testimonials.users.7.text'),
      rating: 5
    },
    {
      name: t('home.testimonials.users.8.name'),
      role: t('home.testimonial_roles.young_mother'),
      text: t('home.testimonials.users.8.text'),
      rating: 5
    },
    {
      name: t('home.testimonials.users.9.name'),
      role: t('home.testimonial_roles.athlete'),
      text: t('home.testimonials.users.9.text'),
      rating: 5
    }
  ], [t]);

  const addToRefs = (el) => {
    if (el && !sectionRefs.current.includes(el)) {
      sectionRefs.current.push(el);
    }
  };

  const isElementInViewport = (el) => {
    const rect = el.getBoundingClientRect();
    return (
      rect.top <= (window.innerHeight || document.documentElement.clientHeight) * 0.8 &&
      rect.bottom >= 0
    );
  };

  const handleScroll = useCallback(() => {
    if (!sectionRefs.current.length) return;
    window.requestAnimationFrame(() => {
      sectionRefs.current.forEach((section) => {
        if (section && isElementInViewport(section)) {
          section.classList.add('visible');
        }
      });
    });
  }, []);

  useEffect(() => {
    let scrollTimeout;
    const debouncedScroll = () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(handleScroll, 10);
    };

    window.addEventListener('scroll', debouncedScroll);
    window.addEventListener('resize', debouncedScroll);
    setTimeout(handleScroll, 300);

    return () => {
      window.removeEventListener('scroll', debouncedScroll);
      window.removeEventListener('resize', debouncedScroll);
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
  }, [handleScroll]);

  useEffect(() => {
    const timer = setTimeout(() => setIsInitialLoad(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    document.body.classList.add('is-home');
    return () => document.body.classList.remove('is-home');
  }, []);

  const handleTestimonialScroll = (direction) => {
    const container = testimonialsScrollRef.current;
    if (!container) return;
    const card = container.querySelector('.testimonial-card');
    const scrollAmount = card ? card.offsetWidth + 16 : 360;
    const newScrollPosition = direction === 'left'
      ? container.scrollLeft - scrollAmount
      : container.scrollLeft + scrollAmount;
    const newIndex = Math.round(newScrollPosition / scrollAmount);
    setActiveTestimonialIndex(Math.max(0, Math.min(newIndex, testimonials.length - 1)));
    container.scrollTo({ left: newScrollPosition, behavior: 'smooth' });
  };

  const scrollToTestimonial = (index) => {
    const container = testimonialsScrollRef.current;
    if (!container) return;
    const card = container.querySelector('.testimonial-card');
    const scrollAmount = (card ? card.offsetWidth + 16 : 360) * index;
    container.scrollTo({ left: scrollAmount, behavior: 'smooth' });
    setActiveTestimonialIndex(index);
  };

  useEffect(() => {
    const container = testimonialsScrollRef.current;
    if (!container) return;
    const onScroll = () => {
      const card = container.querySelector('.testimonial-card');
      const width = card ? card.offsetWidth + 16 : 360;
      setActiveTestimonialIndex(Math.round(container.scrollLeft / width));
    };
    container.addEventListener('scroll', onScroll);
    return () => container.removeEventListener('scroll', onScroll);
  }, []);

  const getLocalizedLink = (path) => `/${language}${path === '/' ? '' : path}`;

  const handleHeroMove = (event) => {
    if (reduceMotion) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const next = {
      x: ((event.clientX - rect.left) / rect.width) * 100,
      y: ((event.clientY - rect.top) / rect.height) * 100
    };
    setSpot((prev) => {
      if (Math.abs(prev.x - next.x) < 1.5 && Math.abs(prev.y - next.y) < 1.5) {
        return prev;
      }
      return next;
    });
  };

  const valueCards = t('home.value_section.cards', { returnObjects: true });
  const personalFeatures = t('home.pricing.packages.personal.features', { returnObjects: true });
  const ctaFeatures = Array.isArray(personalFeatures) ? personalFeatures.slice(0, 4) : [];
  const fallbackFeatures = [
    'Talk by voice or text 24/7',
    'Short check-ins with instant clarity',
    'Personal insights and progress tracking',
    'Thought journal for reflection',
    'Guidance adapted to your situation',
    'Ukrainian, English, German, Lithuanian',
    'Private by design'
  ];

  return (
    <div className={`home-page ${isInitialLoad ? 'initial-load' : ''}`}>
      <SEO
        title={t('seo.home.title')}
        description={t('seo.home.description')}
        jsonLd={homeJsonLd}
      />

      <section
        className="hero-section"
        id="hero"
        onMouseMove={handleHeroMove}
        style={{ '--spot-x': `${spot.x}%`, '--spot-y': `${spot.y}%` }}
      >
        <div className="hero-background" aria-hidden="true">
          <div className="hero-aurora">
            <AuroraCanvas />
            <span className="hero-aurora__spot" />
            <span className="hero-aurora__blob hero-aurora__blob--a" />
            <span className="hero-aurora__blob hero-aurora__blob--b" />
            <span className="hero-aurora__blob hero-aurora__blob--c" />
            <span className="hero-aurora__grid" />
            <span className="hero-aurora__noise" />
          </div>
        </div>
        <div className="container">
          <div className="hero-content">
            <motion.div className="hero-text" {...staggerParent(reduceMotion)}>
              <motion.p className="hero-eyebrow" {...staggerItem(reduceMotion)}>
                {t('home.cta_section.eyebrow')}
              </motion.p>
              <motion.h1
                className="hero-title"
                {...staggerItem(reduceMotion)}
                dangerouslySetInnerHTML={{ __html: t('home.title') }}
              />
              <motion.p className="hero-subtitle" {...staggerItem(reduceMotion)}>
                {t('home.subtitle')}
              </motion.p>

              <motion.dl className="hero-stats" {...staggerItem(reduceMotion)}>
                <div className="stat-item">
                  <dt className="stat-number">24/7</dt>
                  <dd className="stat-label">{t('home.stats.availability')}</dd>
                </div>
                <div className="stat-item">
                  <dt className="stat-number">100%</dt>
                  <dd className="stat-label">{t('home.stats.confidentiality')}</dd>
                </div>
                <div className="stat-item">
                  <dt className="stat-number">70K+</dt>
                  <dd className="stat-label">{t('home.stats.free_subscriptions', t('home.stats.active_users'))}</dd>
                </div>
              </motion.dl>

              <motion.div className="hero-cta-row" {...staggerItem(reduceMotion)}>
                <AppCtaLink
                  platform="web"
                  placement="home_hero"
                  className="btn btn-primary btn-large"
                >
                  {t('home.cta')}
                </AppCtaLink>
                <Link to={getLocalizedLink('/clinical-safety')} className="btn btn-ghost">
                  {t('home.learn_more')}
                </Link>
              </motion.div>

              <motion.div className="hero-app-stores" {...staggerItem(reduceMotion)}>
                <div className="hero-app-stores-row">
                  <AppCtaLink
                    platform="android"
                    placement="home_hero"
                    className="hero-app-store-link"
                    aria-label={t('home.value_section.apps.google')}
                  >
                    <img
                      src="/images/google_en.svg"
                      alt=""
                      className="hero-app-store-badge"
                      width="155"
                      height="46"
                      decoding="async"
                    />
                    <span className="hero-app-store-label">{t('home.value_section.apps.google')}</span>
                  </AppCtaLink>
                  <AppCtaLink
                    platform="ios"
                    placement="home_hero"
                    className="hero-app-store-link"
                    aria-label={t('home.value_section.apps.apple')}
                  >
                    <img
                      src="/images/apple_en.svg"
                      alt=""
                      className="hero-app-store-badge"
                      width="155"
                      height="46"
                      decoding="async"
                    />
                    <span className="hero-app-store-label">{t('home.value_section.apps.apple')}</span>
                  </AppCtaLink>
                </div>
                <div className="hero-app-stores-row hero-web-row">
                  <div className="hero-languages-info">
                    <span className="hero-languages-label">{t('home.app_stores.languages_support')}</span>
                    <div className="hero-languages-badges">
                      <span className="hero-language-badge">UA</span>
                      <span className="hero-language-badge">EN</span>
                      <span className="hero-language-badge">DE</span>
                      <span className="hero-language-badge">LT</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            <Reveal className="hero-image" y={40} delay={0.2}>
              <div className="hero-glow" aria-hidden="true" />
              <aside className="hero-float-chips" aria-hidden="true">
                <span className="hero-chip hero-chip--a">24/7</span>
                <span className="hero-chip hero-chip--b">{t('home.stats.confidentiality')}</span>
                <span className="hero-chip hero-chip--c">70K+</span>
              </aside>
              <div className="app-showcase-host">
                <AppShowcasePhone />
              </div>
            </Reveal>
          </div>
        </div>
        <div className="hero-scroll-cue" aria-hidden="true">
          <span />
        </div>
      </section>

      <section className="hero-message-section">
        <div className="container">
          <Reveal className="hero-message-card">
            <h2>{t('home.hero_message.title')}</h2>
            <p>{t('home.hero_message.subtitle')}</p>
          </Reveal>
        </div>
      </section>

      <section className="value-section" id="value">
        <div className="container">
          <Reveal className="value-header">
            <h2 className="section-title">{t('home.value_section.title')}</h2>
            <p
              className="section-subtitle"
              dangerouslySetInnerHTML={{ __html: t('home.value_section.subtitle') }}
            />
          </Reveal>
          <div className="value-highlights">
            <Reveal className="bento-card bento-card--accent" delay={0.05}>
              <div className="value-highlight-icon" aria-hidden="true">
                <FaChartLine />
              </div>
              <div>
                <h3>{t('home.value_section.highlights.0.title')}</h3>
                <p>{t('home.value_section.highlights.0.description')}</p>
              </div>
            </Reveal>
            <Reveal className="bento-card bento-card--accent" delay={0.1}>
              <div className="value-highlight-icon" aria-hidden="true">
                <FaClipboardCheck />
              </div>
              <div>
                <h3>{t('home.value_section.highlights.1.title')}</h3>
                <p>{t('home.value_section.highlights.1.description')}</p>
              </div>
            </Reveal>
          </div>

          <div className="value-phones" role="list">
            {valueScreens.map((screen, index) => (
              <Reveal
                key={screen.title}
                as="figure"
                className={`phone-shot${index === 0 ? ' phone-shot--lead' : ''}`}
                delay={0.06 + index * 0.05}
                role="listitem"
              >
                <div className="phone-shot__device">
                  <img
                    src={screen.src}
                    alt={screen.title}
                    width="1284"
                    height="2778"
                    loading={screen.eager ? 'eager' : 'lazy'}
                    decoding="async"
                  />
                </div>
                <figcaption>
                  <h3>{screen.title}</h3>
                  <p>{screen.description}</p>
                </figcaption>
              </Reveal>
            ))}
          </div>

          <div className="value-features">
            {Array.isArray(valueCards)
              ? valueCards.map((card, index) => (
                  <Reveal key={index} className="bento-card bento-card--wide" delay={0.08 + index * 0.06}>
                    <h3>{card.title}</h3>
                    <p>{card.description}</p>
                    <ul>
                      {Array.isArray(card.bullets)
                        ? card.bullets.map((item, itemIndex) => (
                            <li key={itemIndex}>
                              <FaCheck className="feature-icon" aria-hidden="true" />
                              <span>{item}</span>
                            </li>
                          ))
                        : null}
                    </ul>
                  </Reveal>
                ))
              : null}
          </div>
          <div className="value-apps">
            <div className="value-apps-text">
              <h3>{t('home.value_section.apps.title')}</h3>
              <p>{t('home.value_section.apps.subtitle')}</p>
            </div>
            <div className="value-apps-links">
              <AppCtaLink
                platform="android"
                placement="home_value_section"
                className="value-apps-link"
                aria-label={t('home.app_stores.google_play')}
              >
                <img
                  src="/images/google_en.svg"
                  alt=""
                  className="app-store-badge"
                  width="155"
                  height="46"
                  loading="lazy"
                  decoding="async"
                />
                <span>{t('home.value_section.apps.google')}</span>
              </AppCtaLink>
              <AppCtaLink
                platform="ios"
                placement="home_value_section"
                className="value-apps-link"
                aria-label={t('home.app_stores.app_store')}
              >
                <img
                  src="/images/apple_en.svg"
                  alt=""
                  className="app-store-badge"
                  width="155"
                  height="46"
                  loading="lazy"
                  decoding="async"
                />
                <span>{t('home.value_section.apps.apple')}</span>
              </AppCtaLink>
            </div>
          </div>
          <div className="value-cta">
            <p>{t('home.value_section.cta_note')}</p>
            <AppCtaLink
              platform="web"
              placement="home_value_section"
              className="btn btn-primary"
            >
              {t('home.value_section.cta')}
            </AppCtaLink>
          </div>
        </div>
      </section>

      <HowItWorks />
      <UserCategories />

      <section className="pricing-section" id="pricing">
        <div className="container">
          <h2 className="section-title" ref={addToRefs}>{t('home.pricing.title')}</h2>
          <p className="section-description" ref={addToRefs}>
            {t('home.pricing.subtitle')}
          </p>
          <div className="pricing-grid">
            <article className="pricing-card pricing-card-main" data-plan="personal">
              <div className="pricing-card-header">
                <h3 className="plan-name">{t('home.pricing.packages.personal.name')}</h3>
                <p className="plan-tagline">{t('home.pricing.packages.personal.tagline')}</p>
              </div>
              <div className="pricing-card-features">
                <ul>
                  {(Array.isArray(personalFeatures) ? personalFeatures : fallbackFeatures).map((feature, index) => (
                    <li key={index}>
                      <FaCheck className="feature-icon" aria-hidden="true" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="pricing-card-cta">
                <AppCtaLink
                  platform="web"
                  placement="home_pricing"
                  className="btn btn-primary btn-large"
                >
                  {t('home.pricing.packages.personal.cta')}
                </AppCtaLink>
              </div>
            </article>
          </div>
          <div className="free-support-info">
            <div className="info-icon" aria-hidden="true">
              <FaStar />
            </div>
            <div className="info-text">
              <p>{t('home.pricing.free_for_ukrainians')}</p>
              <p className="mission-statement">{t('home.pricing.mission_statement')}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="testimonials-section" id="testimonials">
        <div className="container">
          <h2 className="section-title" ref={addToRefs}>{t('home.testimonials.title')}</h2>
          <p className="section-description" ref={addToRefs}>
            {t('home.testimonials.description')}
          </p>
          <div
            className="testimonials-container"
            role="region"
            aria-roledescription="carousel"
            aria-label={t('home.testimonials.title')}
          >
            <button
              type="button"
              className="scroll-button prev"
              onClick={() => handleTestimonialScroll('left')}
              aria-label={t('home.testimonials.scroll.prev')}
            >
              {t('home.testimonials.scroll.prev')}
            </button>
            <div
              className="testimonials-scroll"
              ref={testimonialsScrollRef}
              tabIndex={0}
              aria-live="polite"
            >
              {testimonials.map((testimonial, index) => (
                <article
                  key={index}
                  className="testimonial-card"
                  aria-hidden={index !== activeTestimonialIndex ? 'false' : 'false'}
                >
                  <StarRating rating={testimonial.rating} />
                  <p className="testimonial-text">{testimonial.text}</p>
                  <footer className="testimonial-author-info">
                    <div className="author-name">{testimonial.name}</div>
                    <div className="author-role">{testimonial.role}</div>
                  </footer>
                </article>
              ))}
            </div>
            <button
              type="button"
              className="scroll-button next"
              onClick={() => handleTestimonialScroll('right')}
              aria-label={t('home.testimonials.scroll.next')}
            >
              {t('home.testimonials.scroll.next')}
            </button>
            <div className="scroll-indicators" role="tablist" aria-label={t('home.testimonials.title')}>
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  className={`indicator ${index === activeTestimonialIndex ? 'active' : ''}`}
                  onClick={() => scrollToTestimonial(index)}
                  aria-label={`${index + 1} / ${testimonials.length}`}
                  aria-current={index === activeTestimonialIndex ? 'true' : undefined}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <HelpCategories />
      <FAQ />
      <div className="container">
        <CrisisSupport language={language} />
      </div>
      <Partners />
      <ContactForm />

      <section className="cta-section">
        <div className="container">
          <div className="cta-panel" ref={addToRefs}>
            <header className="cta-panel-header">
              <p className="cta-eyebrow">{t('home.cta_section.eyebrow')}</p>
              <h2 className="cta-title">{t('home.cta_section.title')}</h2>
              <p className="cta-subtitle">{t('home.cta_section.subtitle')}</p>
            </header>
            <div className="cta-card">
              <p className="cta-price">
                <span className="cta-price-amount">{t('home.cta_section.price_amount')}</span>
                <span className="cta-price-period">{t('home.cta_section.price_period')}</span>
              </p>
              <p className="cta-price-note">{t('home.cta_section.price_note')}</p>
              <ul className="cta-features">
                {ctaFeatures.map((feature, index) => {
                  const Icon = [FaClipboardCheck, FaChartLine, FaCheck, FaStar][index] || FaCheck;
                  return (
                    <li key={index}>
                      <span className="cta-feature-icon" aria-hidden="true">
                        <Icon />
                      </span>
                      <span>{feature}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className="cta-actions">
              <AppCtaLink
                platform="web"
                placement="home_cta_glass"
                className="btn btn-primary btn-large"
              >
                {t('home.cta_section.go_to_app')}
              </AppCtaLink>
              <Link to={getLocalizedLink('/news')} className="btn btn-ghost">
                {t('home.cta_section.learn_more')}
              </Link>
            </div>
            <aside className="cta-access">
              <div className="cta-access-icon" aria-hidden="true">
                <FaStar />
              </div>
              <div>
                <strong>{t('home.cta_section.free_title')}</strong>
                <p>{t('home.pricing.free_for_ukrainians')}</p>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <AppDownloadPopup />
    </div>
  );
};

export default HomePage;
